import React, { useEffect } from 'react';
import { RotateCw, X, Sparkles, CheckCircle2, Clock } from 'lucide-react';
import { formatAppUpdateTimestamp } from '../lib/app-version';
import { useDialogA11y } from '../lib/useDialogA11y';

export interface StandaloneAppUpdateModalProps {
  isOpen: boolean;
  onUpdate: () => void | Promise<void>;
  onClose: () => void;
  isUpdating?: boolean;
  serverVersion?: string;
  serverCommit?: string;
  serverBuildTime?: string;
  clientBuildTime?: string;
}

export const StandaloneAppUpdateModal: React.FC<StandaloneAppUpdateModalProps> = ({
  isOpen,
  onUpdate,
  onClose,
  isUpdating = false,
  serverVersion,
  serverCommit,
  serverBuildTime,
  clientBuildTime,
}) => {
  // Escape lukker modalen, men ikke mens opdateringen kører
  const { ref: dialogRef } = useDialogA11y({ open: isOpen, onClose, closeOnEscape: !isUpdating });

  if (!isOpen) return null;

  return (
    <div
      ref={dialogRef}
      className="mobile-inspector-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="app-update-dialog-title"
      onClick={() => {
        if (!isUpdating) onClose();
      }}
      style={{
        zIndex: 'calc(var(--z-popover) + 20)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px',
        background: 'rgba(0, 0, 0, 0.72)',
        backdropFilter: 'blur(6px)',
      }}
    >
      <div
        className="app-update-modal-card"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: 440,
          background: 'linear-gradient(180deg, #1b3823 0%, #0d1e13 100%)',
          border: '1.5px solid rgba(74, 222, 128, 0.4)',
          borderRadius: 20,
          padding: '24px 22px',
          color: '#ffffff',
          boxShadow: '0 20px 50px rgba(0, 0, 0, 0.65), 0 0 30px rgba(22, 163, 74, 0.15)',
          animation: 'slideUpMobileSheet 0.22s cubic-bezier(0.16, 1, 0.3, 1)',
        }}
      >
        {/* Header med ikon og lukkeknap */}
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            marginBottom: 16,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div
              style={{
                width: 48,
                height: 48,
                borderRadius: 14,
                background: 'linear-gradient(135deg, #16a34a 0%, #0e5b2a 100%)',
                border: '1.5px solid rgba(74, 222, 128, 0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#ffffff',
                boxShadow: '0 4px 16px rgba(22, 163, 74, 0.4)',
                flexShrink: 0,
              }}
            >
              <RotateCw size={24} className={isUpdating ? 'animate-spin' : ''} />
            </div>
            <div>
              <div
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                  fontSize: '0.72rem',
                  fontWeight: 800,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  color: '#4ade80',
                  marginBottom: 3,
                }}
              >
                <Sparkles size={12} />
                <span>Ny deployment tilgængelig</span>
              </div>
              <h2
                id="app-update-dialog-title"
                style={{
                  margin: 0,
                  fontSize: '1.22rem',
                  fontWeight: 800,
                  color: '#ffffff',
                  letterSpacing: '-0.01em',
                  lineHeight: 1.25,
                }}
              >
                Opdater Plotwise app
              </h2>
            </div>
          </div>

          {!isUpdating && (
            <button
              type="button"
              onClick={onClose}
              style={{
                background: 'rgba(255, 255, 255, 0.08)',
                border: '1px solid rgba(255, 255, 255, 0.12)',
                borderRadius: 10,
                width: 44,
                height: 44,
                minWidth: 44,
                minHeight: 44,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#cbd5e1',
                cursor: 'pointer',
              }}
              aria-label="Luk opdateringsbesked"
            >
              <X size={18} />
            </button>
          )}
        </div>

        {/* Beskrivelse og fordele */}
        <p
          style={{
            margin: '0 0 16px 0',
            fontSize: '0.88rem',
            color: '#cbd5e1',
            lineHeight: 1.45,
          }}
        >
          Der er udgivet en ny opdatering til Plotwise med nye forbedringer og rettelser. Tryk på{' '}
          <strong style={{ color: '#ffffff' }}>&quot;Opdater nu&quot;</strong> for at hente de
          seneste ændringer direkte til din app.
        </p>

        {/* Feature bullets */}
        <div
          style={{
            background: 'rgba(255, 255, 255, 0.04)',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            borderRadius: 12,
            padding: '12px 14px',
            marginBottom: 20,
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              fontSize: '0.82rem',
              color: '#e2e8f0',
            }}
          >
            <CheckCircle2 size={16} style={{ color: '#4ade80', flexShrink: 0 }} />
            <span>Henter seneste kortdata, rettelser og forbedringer</span>
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              fontSize: '0.82rem',
              color: '#e2e8f0',
            }}
          >
            <CheckCircle2 size={16} style={{ color: '#4ade80', flexShrink: 0 }} />
            <span>Tømmer browser-cache automatisk for et rent miljø</span>
          </div>
          {(serverCommit || serverVersion || serverBuildTime || clientBuildTime) && (
            <div
              style={{
                fontSize: '0.74rem',
                color: '#94a3b8',
                marginTop: 2,
                paddingTop: 8,
                borderTop: '1px solid rgba(255, 255, 255, 0.08)',
                display: 'flex',
                flexDirection: 'column',
                gap: 4,
              }}
            >
              {serverBuildTime && (
                <div
                  style={{
                    color: '#4ade80',
                    fontWeight: 650,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 5,
                  }}
                >
                  <Clock size={12} />
                  <span>Ny udgivelse: {formatAppUpdateTimestamp(serverBuildTime)}</span>
                </div>
              )}
              {clientBuildTime && (
                <div style={{ color: '#cbd5e1' }}>
                  Senest opdateret på denne enhed: {formatAppUpdateTimestamp(clientBuildTime)}
                </div>
              )}
              {(serverCommit || serverVersion) && (
                <div style={{ fontFamily: 'monospace', fontSize: '0.72rem', color: '#64748b' }}>
                  Udgivelse: {serverCommit ? serverCommit.slice(0, 7) : serverVersion}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Handlingsknapper */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <button
            type="button"
            disabled={isUpdating}
            onClick={() => void onUpdate()}
            style={{
              width: '100%',
              minHeight: 48,
              background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)',
              border: 'none',
              borderRadius: 12,
              color: '#ffffff',
              fontSize: '0.96rem',
              fontWeight: 800,
              cursor: isUpdating ? 'wait' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              boxShadow: '0 4px 18px rgba(22, 163, 74, 0.45)',
              opacity: isUpdating ? 0.8 : 1,
            }}
          >
            <RotateCw size={18} className={isUpdating ? 'animate-spin' : ''} />
            <span>{isUpdating ? 'Opdaterer app nu…' : 'Opdater nu'}</span>
          </button>

          {!isUpdating && (
            <button
              type="button"
              onClick={onClose}
              style={{
                width: '100%',
                minHeight: 44,
                background: 'transparent',
                border: '1px solid rgba(255, 255, 255, 0.14)',
                borderRadius: 10,
                color: '#cbd5e1',
                fontSize: '0.86rem',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Senere
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
